import { inject, Injectable, Injector } from "@angular/core";
import { BudgetCounterDto } from "@dtos/simpleData.dto";
import { UserUnreadNotificationsResponseDto } from "@dtos/notifications";
import { IncidenceMainType, incidencesMainTypeDescriptions } from "@enums/incidences.enum";
import { EndPoints } from "@services/EndPoints";
import { ApiService } from "@services/index";
import { Helpers } from "@utils/helpers";

export type IncidenceTypesRecord = Partial<Record<IncidenceMainType, { label: string; items: { value: string; label: string; hasQuantity?: boolean }[] }>>;

interface CachedIncidenceTypes {
    data: IncidenceTypesRecord;
    expiresAt: number;
}

const INCIDENCE_TYPES_STORAGE_KEY = 'roadit_incidence_types_cache';
const INCIDENCE_TYPES_TTL_MS = 2 * 60 * 60 * 1000; // 2 horas

@Injectable({ providedIn: 'root' })
export class GlobalDataProvider {
    private injector = inject(Injector);

    async getTypes(forceRefresh: boolean = false): Promise<IncidenceTypesRecord> {
        // 1. Comprobar caché en sessionStorage si no es refresco forzado
        if (!forceRefresh) {
            const cached = Helpers.getSessionStorage<CachedIncidenceTypes>(INCIDENCE_TYPES_STORAGE_KEY);
            if (cached && cached.expiresAt && Date.now() < cached.expiresAt && cached.data) {
                return cached.data;
            }
        }

        const api = this.injector.get(ApiService);
        const types: IncidenceTypesRecord = {};

        for (const key of Object.keys(incidencesMainTypeDescriptions)) {
            types[(key as IncidenceMainType)] = {
                label: incidencesMainTypeDescriptions[key as IncidenceMainType],
                items: []
            };

            try {
                const result = await api.get<{ value: string; label: string }[]>({
                    url: `${EndPoints.getIncidenceSubtypes}?main_type=${key}`
                });

                if (result) {
                    types[(key as IncidenceMainType)]!.items = result;
                    for (const subtype of types[(key as IncidenceMainType)]!.items) {
                        let url = EndPoints.calculateAmmount
                            .replace("{reason}", key.toString())
                            .replace("{reasonSubType}", subtype.value.toString())
                            .replace("&transport_id={transportId}", "")
                            .replace("&quantity={quantity}", "");

                        try {
                            const r = await api.get<{ value: string; label: string }[]>({ url });
                            if (r) {
                                subtype.hasQuantity = true;
                            }
                        } catch (error) {
                            console.error(error);
                        }
                    }
                }
            } catch (error) {
                console.error(error);
            }
        }

        // 2. Guardar en sessionStorage con validez de 2 horas
        Helpers.setSessionStorage(INCIDENCE_TYPES_STORAGE_KEY, {
            data: types,
            expiresAt: Date.now() + INCIDENCE_TYPES_TTL_MS
        });

        return types;
    }

    async getBudgetCounters(){
        const api = this.injector.get(ApiService);
        try {
            const result = await api.get<BudgetCounterDto>({
                url: EndPoints.getBadgetCounters
            });
            if(result){
                return result;
            }
            throw new Error("Error al obtener los contadores del presupuesto");
        } catch (error) {
            throw error;
        }
    }

    /**
     * Obtiene las notificaciones no leídas del usuario logueado
     */
    async getUnreadNotifications(): Promise<UserUnreadNotificationsResponseDto> {
        const api = this.injector.get(ApiService);
        try {
            const response = await api.get<any>({
                url: EndPoints.listUnreadNotifications
            });
            return UserUnreadNotificationsResponseDto.fromJson(response);
        } catch (error) {
            console.error('Error al obtener notificaciones no leídas:', error);
            return new UserUnreadNotificationsResponseDto();
        }
    }

    /**
     * Marca una notificación como leída
     */
    async markNotificationAsRead(notificationId: number): Promise<boolean> {
        const api = this.injector.get(ApiService);
        try {
            const url = EndPoints.markNotificationAsRead.replace("{notificationId}", notificationId.toString());
            await api.post<any>({ url, body: {} });
            return true;
        } catch (error) {
            console.error('Error al marcar notificación como leída:', error);
            return false;
        }
    }

}
